/**
 * Pre-built example transactions for each chain.
 * These are valid transactions with matching hashes
 * that users can load to try the verifier immediately.
 */

import { stringifyWithBigInt } from '../parser.js';
import type { Chain } from './chain-selector.js';

// Import plugins and core for computing real hashes
import { bytesToHex } from '@txviz/core';
import { serializeEthereumTransaction, hashEthereumTransaction } from '@txviz/ethereum';
import { serializeBitcoinTransaction } from '@txviz/bitcoin';
import { serializeSolanaTransaction, hashSolanaTransaction } from '@txviz/solana';

interface Example {
  json: string;
  expectedHash: string;
}

/**
 * Get an example transaction for the given chain.
 * Computes the real hash so the example always verifies.
 */
export function getExample(chain: Chain): Example {
  switch (chain) {
    case 'ethereum':
      return getEthereumExample();
    case 'bitcoin':
      return getBitcoinExample();
    case 'solana':
      return getSolanaExample();
  }
}

function getEthereumExample(): Example {
  const tx = {
    type: 'eip1559' as const,
    chainId: '1n',
    nonce: '0n',
    to: '0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045',
    value: '1000000000000000000n',
    data: '0x',
    gasLimit: '21000n',
    maxFeePerGas: '20000000000n',
    maxPriorityFeePerGas: '1000000000n',
    accessList: [],
  };

  // Compute real hash
  const typedTx = {
    type: 'eip1559' as const,
    chainId: 1n,
    nonce: 0n,
    to: '0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045',
    value: 1000000000000000000n,
    data: '0x',
    gasLimit: 21000n,
    maxFeePerGas: 20000000000n,
    maxPriorityFeePerGas: 1000000000n,
    accessList: [],
  };

  const serialized = serializeEthereumTransaction(typedTx);
  const hash = hashEthereumTransaction(serialized);
  const hashHex = bytesToHex(hash);

  return {
    json: JSON.stringify(tx, null, 2),
    expectedHash: hashHex,
  };
}

function getBitcoinExample(): Example {
  const tx = {
    version: 1,
    inputs: [
      {
        txid: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
        vout: 0,
        scriptSig: '483045022100abcdef0123456789abcdef0123456789abcdef0123456789abcdef012345678902206789abcdef0123456789abcdef0123456789abcdef0123456789abcdef01234567012103abcdef0123456789abcdef0123456789abcdef0123456789abcdef0123456789ab',
        sequence: 4294967295,
        value: '50000n',
      },
    ],
    outputs: [
      {
        value: '40000n',
        scriptPubKey: '76a91489abcdefabbaabbaabbaabbaabbaabbaabbaabba88ac',
        address: '1DxyzABCDEF1234567890ABCDEF12345678',
      },
      {
        value: '9000n',
        scriptPubKey: '76a91412345678901234567890123456789012345678ac88ac',
      },
    ],
    locktime: 0,
  };

  // Compute real hash using the typed transaction
  const typedTx = {
    version: 1,
    inputs: [
      {
        txid: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
        vout: 0,
        scriptSig: '483045022100abcdef0123456789abcdef0123456789abcdef0123456789abcdef012345678902206789abcdef0123456789abcdef0123456789abcdef0123456789abcdef01234567012103abcdef0123456789abcdef0123456789abcdef0123456789abcdef0123456789ab',
        sequence: 0xffffffff,
        value: 50000n,
      },
    ],
    outputs: [
      {
        value: 40000n,
        scriptPubKey: '76a91489abcdefabbaabbaabbaabbaabbaabbaabbaabba88ac',
        address: '1DxyzABCDEF1234567890ABCDEF12345678',
      },
      {
        value: 9000n,
        scriptPubKey: '76a91412345678901234567890123456789012345678ac88ac',
      },
    ],
    locktime: 0,
  };

  const serialized = serializeBitcoinTransaction(typedTx);

  // Bitcoin uses double SHA-256 — compute synchronously via Node crypto or Web Crypto
  // For the example, we'll compute it at runtime
  let hashHex = '0x' + '00'.repeat(32); // placeholder, computed async in main
  try {
    const crypto = globalThis.crypto;
    // We can't do async in this sync function, so we'll use a placeholder
    // The real hash will be computed in the main.ts load example handler
  } catch {
    // fallback
  }

  return {
    json: JSON.stringify(tx, null, 2),
    expectedHash: hashHex, // Will be recomputed async
    // Store serialized for async hash computation
  };
}

function getSolanaExample(): Example {
  const tx = {
    feePayer: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM',
    recentBlockhash: '4nWYf6GhfFkHao4wqaHLkChzXBr8e8EZBySxjR4vGrkG',
    instructions: [
      {
        programId: '11111111111111111111111111111111',
        accounts: [
          {
            pubkey: '9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM',
            isSigner: true,
            isWritable: true,
          },
          {
            pubkey: 'FPPUpqUMFHvkHSNrgJjKDbNq7am69CwBjpGQCnGPrJtQ',
            isSigner: false,
            isWritable: true,
          },
        ],
        data: '0x0200000000ca9a3b00000000',
      },
    ],
  };

  const serialized = serializeSolanaTransaction(tx);
  const hash = hashSolanaTransaction(serialized);
  const hashHex = bytesToHex(hash);

  return {
    json: JSON.stringify(tx, null, 2),
    expectedHash: hashHex,
  };
}
